/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { useCallback, useEffect, useState } from 'react';
import { Monitor, Plus, X } from 'lucide-react';
import { ApiError, api } from '../lib/api';
import { errorText } from '../lib/errors';
import {
  MCP_READ_ONLY_HINT,
  MCP_READ_ONLY_SAVED_TEXT,
  MCP_READ_ONLY_SEED_TEXT,
  MCP_READ_ONLY_STORED_TEXT,
  parseRows,
  rowsEqual,
  rowsOf,
  type McpReadOnlyRow,
  type McpReadOnlyView,
} from '../lib/mcpReadOnly';
import { Button, Callout, Field, Icon, Input, Panel, Skeleton, StatusChip, Textarea } from '../ui';
import { SaveNote } from './SaveNote';

type Read =
  | { status: 'loading' }
  /** A service without the route (an older one): the section is not shown. */
  | { status: 'absent' }
  | { status: 'failed'; text: string }
  | { status: 'loaded'; view: McpReadOnlyView };

/**
 * Apps > "Connections on PCs: read-only tools" (owner, 2026-10-18 18:55 PT: "normal read activites for revit should not
 * require acceptance only writes"): per native MCP server on the PCs, the tools whose calls run without a confirmation
 * card, each always a read or a read only for named values of one argument (run_revit_python when mode is read or
 * preview). The seed (Gyro AI for Revit's reads) is in effect until a list is stored; "Use the seed" puts it back. The
 * PC still re-checks every inline read against its own allow-list, so this list can only name what the PC also knows.
 */
export function McpReadOnlyToolsSection({ locked }: { locked: boolean }) {
  const [read, setRead] = useState<Read>({ status: 'loading' });
  const [rows, setRows] = useState<McpReadOnlyRow[]>([]);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);

  const show = useCallback((view: McpReadOnlyView) => {
    setRead({ status: 'loaded', view });
    setRows(rowsOf(view.servers));
  }, []);

  useEffect(() => {
    let live = true;
    api.mcpReadOnlyTools().then(
      (view) => { if (live) show(view); },
      (error) => {
        if (!live) return;
        if (error instanceof ApiError && error.status === 404) setRead({ status: 'absent' });
        else setRead({ status: 'failed', text: errorText(error, 'The read-only tools could not be read.') });
      },
    );
    return () => { live = false; };
  }, [show]);

  if (read.status === 'absent') return null;

  const view = read.status === 'loaded' ? read.view : null;
  const parsed = view ? parseRows(rows, view.limits) : null;
  const dirty = view ? !rowsEqual(rows, view.servers, view.limits) : false;
  const edit = (index: number, patch: Partial<McpReadOnlyRow>) => { setNote(null); setRows((current) => current.map((row, at) => (at === index ? { ...row, ...patch } : row))); };

  const save = async (servers: McpReadOnlyView['servers'] | null) => {
    setBusy(true);
    setNote(null);
    try {
      const saved = await api.setMcpReadOnlyTools(servers);
      show(saved);
      setNote({ ok: true, text: servers === null ? 'The seed is in effect again.' : MCP_READ_ONLY_SAVED_TEXT });
    } catch (error) {
      setNote({ ok: false, text: errorText(error, 'The list was not saved.') });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title={<span className="ui-title-icon" id="mcp-read-only-title"><Icon icon={Monitor} size="md" />Connections on PCs: read-only tools</span>}
      description="Calls to these tools of a PC's local connections run without a confirmation card; every other call is confirmed with one click"
      actions={view ? (view.isDefault ? <StatusChip tone="neutral">seed</StatusChip> : <StatusChip tone="ok">stored</StatusChip>) : null}
      footer={view ? (
        <span className="ui-actions">
          <Button variant="primary" size="sm" disabled={locked || busy || !dirty || !parsed?.ok} onClick={() => { if (parsed?.ok) void save(parsed.servers); }}>Save</Button>
          <Button variant="ghost" size="sm" disabled={locked || busy || view.isDefault} title="Store nothing: the seed is in effect again" onClick={() => void save(null)}>Use the seed</Button>
          <Button variant="ghost" size="sm" icon={Plus} disabled={locked || busy || rows.length >= view.limits.servers} onClick={() => { setNote(null); setRows((current) => [...current, { server: '', displayName: '', tools: '' }]); }}>Add a server</Button>
          <span className="ui-quiet">{view.isDefault ? MCP_READ_ONLY_SEED_TEXT : MCP_READ_ONLY_STORED_TEXT}</span>
        </span>
      ) : undefined}
    >
      {read.status === 'loading' && <Skeleton />}
      {read.status === 'failed' && <Callout tone="bad" role="alert" title="Could not read the list">{read.text}</Callout>}
      {view && (
        <div className="ui-stack" data-mcp-read-only-rows={rows.length}>
          <p className="ui-quiet">{MCP_READ_ONLY_HINT}</p>
          {rows.length === 0 && <p className="ui-quiet">No server is listed: only the reads the PC itself marks (reviewed Bluebeam reads and tools annotated read-only) run without a card.</p>}
          {rows.map((row, index) => (
            <div key={index} className="ui-grid ui-grid--2" data-mcp-read-only-row={index}>
              <Field label="Server key" help={index === 0 ? 'As the PC lists it under Connections' : undefined}>
                <Input value={row.server} disabled={locked || busy} aria-label={`Server ${index + 1} key`} onChange={(event) => edit(index, { server: event.target.value })} />
              </Field>
              <Field label="Display name" help={index === 0 ? 'What people see on cards' : undefined}>
                <Input value={row.displayName} disabled={locked || busy} aria-label={`Server ${index + 1} display name`} onChange={(event) => edit(index, { displayName: event.target.value })} />
              </Field>
              <Field label="Read-only tools">
                <span className="ui-actions">
                  <Textarea rows={3} value={row.tools} disabled={locked || busy} aria-label={`Server ${index + 1} read-only tools`} onChange={(event) => edit(index, { tools: event.target.value })} />
                  <Button variant="ghost" size="sm" icon={X} aria-label={`Remove server ${index + 1}`} disabled={locked || busy} onClick={() => { setNote(null); setRows((current) => current.filter((_, at) => at !== index)); }} />
                </span>
              </Field>
            </div>
          ))}
          {parsed && !parsed.ok && <Callout tone="warn" role="alert" title={`Row ${parsed.row + 1}`}>{parsed.text}</Callout>}
          <SaveNote note={note} />
        </div>
      )}
    </Panel>
  );
}
